import React, { ReactElement, FC, useContext, useEffect } from 'react';

import { useMediaQuery } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import styles from './styles.module.scss';

import { FundsGraph, CurrenciesTable } from './components';

import { RESEARCH } from 'constants/organizationTabs';

import { useAppDispatch, useAppSelector } from 'store';
import { FormatUtils, LinkUtils } from 'utils';
import { OrganizationContext } from 'context/organizationContext';
import { DaoAssetsTable } from './components/DaoAssetsTable';
import { TreasuryCompositionTable } from './components/TreasuryCompositionTable';
import { TreasuryCompositionGraph } from './components/TreasuryCompositionGraph';
import { OrganizationTreasuryProps } from './types';
import { OrganizationActions } from 'store/actions';
import { mixpanelService } from 'services';
import { DaoElementsCardItems } from 'components/DaoElementsCardItems';
import { IOrganizationSimilarDao } from 'types/interfaces';
import { CarouselWrapper } from 'components/CarouselWrapper';
import { defaultOrganizationPlaceholderImage } from 'assets';

const MAX_DESCRIPTION_LENGTH = 150;

export const OrganizationTreasury: FC<OrganizationTreasuryProps> = ({ isPremiumAllowed }): ReactElement => {
    const dispatch = useAppDispatch();
    const isTablet = useMediaQuery('(max-width: 767.98px)');
    
    const {
        organizationData: { description },
    } = useAppSelector(({ organization }) => organization);

    const {
        organizationSimilarDaos,
        similarDaosLoading,
        organizationData: { id: organizationId },
    } = useAppSelector(({ organization }) => organization);

    const { organizationTreasuryComposition, organizationTreasuryCompositionGraph } = useAppSelector(
        ({ organizationTabs }) => organizationTabs,
    );

    const { changeOrganizationDataTab } = useContext(OrganizationContext);
    const shortDescription = FormatUtils.truncateString(description || '', MAX_DESCRIPTION_LENGTH);

    const handleSimilarOrganizations = () =>
        mixpanelService.track(mixpanelService.eventsGenerator.similarOrganizationsClickEvent());

    useEffect(() => {
        if (!isPremiumAllowed) {
            dispatch(OrganizationActions.getOrganizationSimilarDaosData.request({ organizationId }));
        }
    }, [organizationId, isPremiumAllowed, dispatch]);

    return (
        <div className={styles.container}>
            {description && isTablet && (
                <div className={styles.mobileDescriptionSection}>
                    <div className={styles.textDescription} dangerouslySetInnerHTML={{ __html: shortDescription }} />
                    {description.length > MAX_DESCRIPTION_LENGTH && (
                        <div className={styles.readMore} onClick={() => changeOrganizationDataTab(RESEARCH)}>
                            <span className={styles.text}>Read More</span>
                            <ArrowForwardIcon className={styles.arrowIcon} />
                        </div>
                    )}
                </div>
            )}
            {!isPremiumAllowed && (
                <div className={styles.carouselsWrapper} onClick={handleSimilarOrganizations}>
                    <CarouselWrapper
                        data={organizationSimilarDaos}
                        loading={similarDaosLoading}
                        title="Similar organizations by number of mutual voters"
                        wrapperClassName={styles.carouselWrapper}
                    >
                        {organizationSimilarDaos.map((data: IOrganizationSimilarDao) => (
                            <DaoElementsCardItems
                                key={data.secondOrganizationId}
                                logo={
                                    LinkUtils.isValidUrl(data.secondOrganizationLogo || '')
                                        ? data.secondOrganizationLogo
                                        : LinkUtils.getDaoLogoLinkFromBucket(
                                              data.secondOrganizationLogo,
                                              defaultOrganizationPlaceholderImage,
                                          )
                                }
                                link={LinkUtils.createDynamicLink(data.secondOrganizationId, 'organization')}
                                name={data.secondOrganizationTitle}
                                members={data.numberOfCommonVoters || 0}
                            />
                        ))}
                    </CarouselWrapper>
                </div>
            )}
            <div className={styles.gridContainer}>
                <div className={styles.gridItem}>
                    <CurrenciesTable />
                </div>

                <div className={styles.gridItem}>
                    <DaoAssetsTable isPremiumAllowed={isPremiumAllowed} />
                </div>
            </div>
            <div className={styles.treasuryCompositionTable}>
                <TreasuryCompositionGraph
                    organizationTreasuryComposition={organizationTreasuryCompositionGraph.data}
                    loading={organizationTreasuryComposition.loading}
                    isPremiumAllowed={isPremiumAllowed}
                />
            </div>
            <div className={styles.treasuryOverTime}>
                <div className={styles.data}>
                    <FundsGraph />
                </div>
            </div>
            <div className={styles.treasuryCompositionTable}>
                <TreasuryCompositionTable
                    organizationTreasuryComposition={organizationTreasuryComposition.data}
                    loading={organizationTreasuryComposition.loading}
                    isPremiumAllowed={isPremiumAllowed}
                />
            </div>
        </div>
    );
};
